import { inject, Injectable } from '@angular/core';
import {
  Address,
  createAddress,
  IdCard,
  IOlavEntry,
  IOrderDetailsInfo,
  IProduktAuswahlDetails,
  IProduktVerfuegbarkeit,
  IRelocationData,
  IUploadResult,
  PreisIndikation,
  Product,
  RelocationPDFLinks,
} from '@mwe/models';
import {
  invalidateSignals,
  SessionStorage,
  SessionStorageService,
  SignalStateProperty,
  SignalStateService,
  sortProductsByCategory,
} from '@mwe/utils';
import { ProductCategoryEnum, ServiceStateEnum } from '@mwe/constants';
import { ProductsWhiteListLogic } from '../products/products-white-list.logic';
import { ConfirmationStatusStateService } from '../confirmation/confirmation-status-state.service';
import { MemoryCacheItem } from '../cache/memory-cache.service';

const relocationInitializedForFromAddressKey = 'relocation_relocationInitializedForAddress';
const addressDetailsLinkTypeKey = 'relocation_addressDetailsLinkType';
const relocationFromAddressKey = 'relocation_relocationFromAddress';
const relocationFromProductsKey = 'relocation_relocationFromProducts';
const relocationToAddressKey = 'relocation_relocationToAddress';
const relocationToOlavKey = 'relocation_relocationToOlav';
const relocationToAvailabilitiesKey = 'relocation_relocationToAvailabilities';
const relocationToAvailabilitiesStateKey = 'relocation_relocationToAvailabilitiesState';
const registrationDateKey = 'relocation_registrationDate';
const deregistrationDateKey = 'relocation_deregistrationDate';
const relocationDetailsKey = 'relocation_details';
const relocationPreviousSelectedProductsKey = 'relocation_previousSelectedProducts';
const relocationNoOnlineCategoriesKey = 'relocation_noOnlineCategories';
const relocationNoLongerAvailableCategories = 'relocation_noLongerAvailableCategories';
const relocationIdCardKey = 'relocation_idCard';
const relocationUploadedFilesKey = 'relocation_uploadedFiles';
const relocationUseSubstituteDetailsKey = 'relocation_useSubstituteDetails';
const relocationPdfLinksKey = 'relocation_pdfLinks';
const approvalDataTransferToStromOrGasKey = 'relocation_approvalDataTransferToStromOrGas';
const relocationLastOrderIdKey = 'relocation_lastOrderId';
const relocationAddressToSetKey = 'relocation_addressToSet';
const relocationSelectedProductsKey = 'relocation_selectedProducts';
const relocationPriceIndicationsKey = 'relocation_priceIndications';
const relocationRetriedOnErrorKey = 'relocation_retriedOnError';
const relocationSubmitIdKey = 'relocation_submitId';

@Injectable({ providedIn: 'root' })
@SignalStateService({ debug: true, decorateAll: false })
export class RelocationStateService extends ConfirmationStatusStateService {
  declare protected storage: SessionStorageService;
  private readonly productsWhiteListLogic = inject(ProductsWhiteListLogic);

  @SessionStorage(relocationAddressToSetKey)
  private _addressSetTo = false;

  @SessionStorage(relocationFromAddressKey)
  private _relocationFromAddress: Address;

  @SessionStorage(relocationInitializedForFromAddressKey)
  private _relocationInitializedForFromAddress = false;

  @SignalStateProperty()
  omitStep = false;

  @SignalStateProperty()
  skipStep = false;

  @SignalStateProperty()
  stepValid = false;

  constructor() {
    const storage = inject(SessionStorageService);

    super(storage, relocationLastOrderIdKey, relocationRetriedOnErrorKey, relocationSubmitIdKey, MemoryCacheItem.ClearingAccountDetails);

    this.storage = storage;
  }

  @SignalStateProperty()
  get relocationFromAddress(): Address {
    return (this._relocationFromAddress && createAddress(this._relocationFromAddress)) || undefined;
  }

  set relocationFromAddress(_relocationFromAddress: Address) {
    if (_relocationFromAddress) {
      if (this._relocationFromAddress !== _relocationFromAddress) {
        this._relocationInitializedForFromAddress = false;
      }
      this._relocationFromAddress = _relocationFromAddress;
    } else {
      this.storage.clear(relocationFromAddressKey);
    }
  }

  @SignalStateProperty()
  get relocationInitializedForFromAddress(): boolean {
    return this._relocationInitializedForFromAddress;
  }

  set relocationInitializedForFromAddress(value: boolean) {
    if (value === undefined || value === null) {
      this.storage.clear(relocationInitializedForFromAddressKey);
      return;
    }
    this._relocationInitializedForFromAddress = value;
  }

  @SessionStorage(relocationFromProductsKey)
  private _relocationFromProducts: Product[];

  @SignalStateProperty()
  get relocationFromProducts(): Product[] {
    return this._relocationFromProducts?.map(elem => elem) || undefined;
  }

  set relocationFromProducts(_relocationFromProducts: Product[]) {
    if (_relocationFromProducts) {
      const whiteListedProducts = this.productsWhiteListLogic.removeNotWhiteListedProducts(_relocationFromProducts);
      this._relocationFromProducts = sortProductsByCategory(whiteListedProducts, true) as Product[];
    } else {
      this.storage.clear(relocationFromProductsKey);
    }
  }

  @SessionStorage(relocationToAddressKey)
  private _relocationToAddress: Address;

  @SignalStateProperty()
  get relocationToAddress(): Address {
    return (this._relocationToAddress && createAddress(this._relocationToAddress)) || undefined;
  }

  set relocationToAddress(_relocationToAddress: Address) {
    if (_relocationToAddress) {
      this._relocationToAddress = _relocationToAddress;
    } else {
      this.storage.clear(relocationToAddressKey);
    }
  }

  @SessionStorage(addressDetailsLinkTypeKey)
  private _addressDetailsLinkType: string;

  @SignalStateProperty()
  get addressDetailsLinkType(): string {
    return this._addressDetailsLinkType;
  }

  set addressDetailsLinkType(addressDetailsLinkType: string) {
    if (addressDetailsLinkType) {
      this._addressDetailsLinkType = addressDetailsLinkType;
    } else {
      this.storage.clear(addressDetailsLinkTypeKey);
    }
  }

  @SessionStorage(relocationToOlavKey)
  private _relocationToOlav: IOlavEntry;

  @SignalStateProperty()
  get relocationToOlav(): IOlavEntry {
    return this._relocationToOlav || undefined;
  }

  set relocationToOlav(relocationToOlav: IOlavEntry) {
    if (relocationToOlav) {
      this._relocationToOlav = relocationToOlav;
    } else {
      this.storage.clear(relocationToOlavKey);
    }
  }

  @SessionStorage(relocationToAvailabilitiesKey)
  private _relocationToAvailabilities: IProduktVerfuegbarkeit[];

  @SignalStateProperty()
  get relocationToAvailabilities(): IProduktVerfuegbarkeit[] {
    return this._relocationToAvailabilities || undefined;
  }

  set relocationToAvailabilities(relocationToAvailabilities: IProduktVerfuegbarkeit[]) {
    if (relocationToAvailabilities) {
      this._relocationToAvailabilities = relocationToAvailabilities;
    } else {
      this.storage.clear(relocationToAvailabilitiesKey);
    }
  }

  @SessionStorage(relocationToAvailabilitiesStateKey)
  private _relocationToAvailabilitiesState: ServiceStateEnum;

  @SignalStateProperty()
  get relocationToAvailabilitiesState(): ServiceStateEnum {
    return this._relocationToAvailabilitiesState || undefined;
  }

  set relocationToAvailabilitiesState(relocationToAvailabilitiesState: ServiceStateEnum) {
    if (relocationToAvailabilitiesState) {
      this._relocationToAvailabilitiesState = relocationToAvailabilitiesState;
    } else {
      this.storage.clear(relocationToAvailabilitiesStateKey);
    }
  }

  @SessionStorage(registrationDateKey)
  private _registrationDate: string;

  @SignalStateProperty()
  get registrationDate(): string {
    return this._registrationDate || undefined;
  }

  set registrationDate(_registrationDate: string) {
    if (_registrationDate) {
      this._registrationDate = _registrationDate;
    } else {
      this.storage.clear(registrationDateKey);
    }
  }

  @SessionStorage(deregistrationDateKey)
  private _deregistrationDate: string;

  @SignalStateProperty()
  get deregistrationDate(): string {
    return this._deregistrationDate || undefined;
  }

  set deregistrationDate(_deregistrationDate: string) {
    if (_deregistrationDate) {
      this._deregistrationDate = _deregistrationDate;
    } else {
      this.storage.clear(deregistrationDateKey);
    }
  }

  @SessionStorage(relocationDetailsKey)
  private _relocationDetails: IOrderDetailsInfo[];

  @SignalStateProperty()
  get relocationDetails(): IOrderDetailsInfo[] {
    return this._relocationDetails || undefined;
  }

  set relocationDetails(relocationDetails: IOrderDetailsInfo[]) {
    if (relocationDetails) {
      this._relocationDetails = relocationDetails;
    } else {
      this.storage.clear(relocationDetailsKey);
    }
  }

  @SessionStorage(relocationPreviousSelectedProductsKey)
  private _previousSelectedProducts: ProductCategoryEnum[];

  @SignalStateProperty()
  get previousSelectedProducts(): ProductCategoryEnum[] {
    return this._previousSelectedProducts || undefined;
  }

  set previousSelectedProducts(previousSelectedProducts: ProductCategoryEnum[]) {
    if (previousSelectedProducts) {
      this._previousSelectedProducts = previousSelectedProducts;
    } else {
      this.storage.clear(relocationPreviousSelectedProductsKey);
    }
  }

  @SessionStorage(relocationNoOnlineCategoriesKey)
  private _noOnlineCategories: string[];

  @SignalStateProperty()
  get noOnlineCategories(): string[] {
    return this._noOnlineCategories || undefined;
  }

  set noOnlineCategories(noOnlineCategories: string[]) {
    if (noOnlineCategories) {
      this._noOnlineCategories = noOnlineCategories;
    } else {
      this.storage.clear(relocationNoOnlineCategoriesKey);
    }
  }

  @SessionStorage(relocationNoLongerAvailableCategories)
  private _noLongerAvailableCategories: string[];

  @SignalStateProperty()
  get noLongerAvailableCategories(): string[] {
    return this._noLongerAvailableCategories || undefined;
  }

  set noLongerAvailableCategories(noLongerAvailableCategories: string[]) {
    if (noLongerAvailableCategories) {
      this._noLongerAvailableCategories = noLongerAvailableCategories;
    } else {
      this.storage.clear(relocationNoLongerAvailableCategories);
    }
  }

  @SessionStorage(relocationIdCardKey)
  private _idCard: IdCard;

  @SignalStateProperty()
  get idCard(): IdCard {
    return this._idCard || undefined;
  }

  set idCard(idCard: IdCard) {
    if (idCard) {
      this._idCard = idCard;
    } else {
      this.storage.clear(relocationIdCardKey);
    }
  }

  @SessionStorage(relocationUploadedFilesKey)
  private _uploadedFiles: IUploadResult;

  @SignalStateProperty()
  get uploadedFiles(): IUploadResult {
    return this._uploadedFiles || undefined;
  }

  set uploadedFiles(uploadedFiles: IUploadResult) {
    if (uploadedFiles) {
      this._uploadedFiles = uploadedFiles;
    } else {
      this.storage.clear(relocationUploadedFilesKey);
    }
  }

  @SessionStorage(relocationUseSubstituteDetailsKey)
  private _useSubstituteDetails: boolean;

  @SignalStateProperty()
  get useSubstituteDetails(): boolean {
    return this._useSubstituteDetails || undefined;
  }

  set useSubstituteDetails(useSubstituteDetails: boolean) {
    if (useSubstituteDetails) {
      this._useSubstituteDetails = useSubstituteDetails;
    } else {
      this.storage.clear(relocationUseSubstituteDetailsKey);
    }
  }

  @SessionStorage(relocationPdfLinksKey)
  private _pdfLinks: RelocationPDFLinks;

  @SignalStateProperty()
  get pdfLinks(): RelocationPDFLinks {
    return this._pdfLinks || undefined;
  }

  set pdfLinks(pdfLinks: RelocationPDFLinks) {
    if (pdfLinks) {
      this._pdfLinks = pdfLinks;
    } else {
      this.storage.clear(relocationPdfLinksKey);
    }
  }

  @SessionStorage(approvalDataTransferToStromOrGasKey)
  private _approvalDataTransferToStromOrGas: boolean;

  @SignalStateProperty()
  get approvalDataTransferToStromOrGas(): boolean {
    return this._approvalDataTransferToStromOrGas || undefined;
  }

  set approvalDataTransferToStromOrGas(_approvalDataTransferToStromOrGas: boolean) {
    if (_approvalDataTransferToStromOrGas) {
      this._approvalDataTransferToStromOrGas = _approvalDataTransferToStromOrGas;
    } else {
      this.storage.clear(approvalDataTransferToStromOrGasKey);
    }
  }

  @SessionStorage(relocationLastOrderIdKey)
  private _lastOrderId: string;

  @SignalStateProperty()
  override get lastOrderId(): string {
    return this._lastOrderId || undefined;
  }

  override set lastOrderId(lastOrderId: string) {
    if (lastOrderId) {
      this._lastOrderId = lastOrderId;
    } else {
      this.storage.clear(relocationLastOrderIdKey);
    }
  }
  @SignalStateProperty()
  get relocationAddressSetTo(): boolean {
    return this._addressSetTo;
  }

  set relocationAddressSetTo(isAddressSetTo: boolean) {
    if (isAddressSetTo) {
      this._addressSetTo = isAddressSetTo;
    } else {
      this.storage.clear(relocationAddressToSetKey);
    }
  }

  @SessionStorage(relocationSelectedProductsKey)
  private _selectedProducts: IProduktAuswahlDetails[];

  @SignalStateProperty()
  get selectedProducts(): IProduktAuswahlDetails[] {
    return this._selectedProducts || undefined;
  }

  set selectedProducts(value: IProduktAuswahlDetails[]) {
    if (value) {
      this._selectedProducts = value;
    } else {
      this.storage.clear(relocationSelectedProductsKey);
    }
  }

  @SessionStorage(relocationPriceIndicationsKey)
  private _priceIndications: PreisIndikation[];

  @SignalStateProperty()
  get priceIndications(): PreisIndikation[] {
    return this._priceIndications || undefined;
  }

  set priceIndications(value: PreisIndikation[]) {
    if (value) {
      this._priceIndications = value;
    } else {
      this.storage.clear(relocationPriceIndicationsKey);
    }
  }

  @SessionStorage(relocationRetriedOnErrorKey)
  private _retriedOnError: boolean;

  override get retriedOnError(): boolean {
    return this._retriedOnError || undefined;
  }

  override set retriedOnError(retriedOnError: boolean) {
    if (retriedOnError) {
      this._retriedOnError = retriedOnError;
    } else {
      this.storage.clear(relocationRetriedOnErrorKey);
    }
  }

  @SessionStorage(relocationSubmitIdKey)
  private _submitId: number;

  override get submitId(): number {
    return this._submitId || undefined;
  }

  override set submitId(submitId: number) {
    if (submitId) {
      this._submitId = submitId;
    } else {
      this.storage.clear(relocationSubmitIdKey);
    }
  }

  clearData(): void {
    this.storage.clear(relocationFromAddressKey);
    this.storage.clear(relocationFromProductsKey);
    this.storage.clear(relocationToAddressKey);
    this.storage.clear(addressDetailsLinkTypeKey);
    this.storage.clear(relocationToOlavKey);
    this.storage.clear(relocationToAvailabilitiesKey);
    this.storage.clear(relocationToAvailabilitiesStateKey);
    this.storage.clear(registrationDateKey);
    this.storage.clear(deregistrationDateKey);
    this.storage.clear(relocationDetailsKey);
    this.storage.clear(relocationPreviousSelectedProductsKey);
    this.storage.clear(relocationNoOnlineCategoriesKey);
    this.storage.clear(relocationIdCardKey);
    this.storage.clear(relocationUploadedFilesKey);
    this.storage.clear(relocationUseSubstituteDetailsKey);
    this.storage.clear(relocationPdfLinksKey);
    this.storage.clear(approvalDataTransferToStromOrGasKey);
    this.storage.clear(relocationLastOrderIdKey);
    this.storage.clear(relocationAddressToSetKey);
    this.storage.clear(relocationSelectedProductsKey);
    this.storage.clear(relocationPriceIndicationsKey);
    this.storage.clear(relocationNoLongerAvailableCategories);
    this.storage.clear(relocationSubmitIdKey);
    this.storage.clear(relocationAddressToSetKey);
    this.storage.clear(relocationInitializedForFromAddressKey);
    invalidateSignals(this);
  }

  clearAll(): void {
    this.clearData();
    super.clear();
  }

  getRelocationData(): IRelocationData {
    return {
      relocationFromAddress: this.relocationFromAddress,
      relocationToOlav: this.relocationToOlav,
      relocationToAddress: this.relocationToAddress,
      registrationDate: this.registrationDate,
      deregistrationDate: this.deregistrationDate,
      relocationDetails: this.relocationDetails,
      idCard: this.idCard,
      uploadedFiles: this.uploadedFiles,
      useSubstituteDetails: this.useSubstituteDetails,
      pdfLinks: this.pdfLinks,
      noOnlineCategories: this.noOnlineCategories,
    };
  }
}
